import * as React from 'react';
import { CircleDashed, FileEdit, Globe2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import type { CopyEntryStatus, CopyLocale } from '@/schemas/admin/copy';

const VARIANT: Record<
  CopyEntryStatus,
  { tone: React.ComponentProps<typeof Badge>['tone']; Icon: React.ComponentType<{ className?: string }> }
> = {
  // Colour is never the only signal (CLAUDE.md accessibility rule): each state
  // carries its own glyph and, in the table, its own locale label.
  clean: { tone: 'neutral', Icon: CircleDashed },
  draft: { tone: 'warn', Icon: FileEdit },
  published: { tone: 'success', Icon: Globe2 },
};

/**
 * One locale's override state. `clean` means "no override — the site renders
 * the shipped baseline", which is the normal state for most of the catalog.
 */
export function CopyStatusChip({
  locale,
  status,
  showLocale = true,
  className,
}: {
  locale: CopyLocale;
  status: CopyEntryStatus;
  showLocale?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('copy');
  const cfg = VARIANT[status];
  const label = t(`status.${status}`);
  return (
    <Badge
      tone={cfg.tone}
      className={cn('gap-1', className)}
      aria-label={`${locale.toUpperCase()} — ${label}`}
    >
      <cfg.Icon className="size-3.5" aria-hidden />
      {showLocale ? (
        <span dir="ltr" className="font-mono text-[10px] font-semibold uppercase">
          {locale}
        </span>
      ) : null}
      <span>{label}</span>
    </Badge>
  );
}

/** The structural-field lock, reused by the object-list editor and its legend. */
export function LockedBadge({ label }: { label: string }) {
  return (
    <Badge tone="neutral" className="gap-1">
      <Lock className="size-3.5" aria-hidden />
      <span>{label}</span>
    </Badge>
  );
}
